import { useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Code as CodeIcon,
  Copy,
  Trash2,
  Check,
  X,
  Pencil,
  Globe,
  Lock,
  ChevronDown,
  MonitorPlay,
  Server,
  Shield,
  ShieldAlert,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { executeUserCode } from "@shared/code/executor";
import {
  createSandboxExecute,
  createUnsafeExecute,
} from "@shared/code/createExecute";
import type { Code } from "@/lib/codes";
import { useFetcher, useRevalidator } from "react-router";
import type { FetcherWithComponents } from "react-router";
import type {
  CodeRunnerErrorPayload,
  CodeRunnerResponsePayload,
  CodeRunnerExecutionError,
  SupabaseFunctionExecutionError,
  ExecutionError,
  ExecutionResult,
  GenericExecutionError,
  ConsoleLogEntry,
  ConsoleLogLevel,
  JsonValue,
} from "@shared/code/types";
import {
  WORKSPACE_SIDEBAR_ACTION_PATH,
  workspaceSidebarAction,
  type WorkspaceSidebarActionIntent,
  type WorkspaceSidebarActionResult,
} from "@/routes/workspaceSidebarAction";
import { supabase } from "@shared/integrations/supabase/client";
import { invokeFunction } from "@shared/functions.ts";
import CodeRunnerContract from "@shared/functions/code-runner.ts";
import {
  SupabaseFunctionError,
  SupabaseParseError,
  isSupabaseResponseError,
  type ParsedSupabaseFunctionError,
} from "@shared/functions/supabaseFunctionError.ts";

export type { Code } from "@/lib/codes";

interface CodeViewProps {
  code: Code;
  userEmail?: string | undefined;
  onUpdate?: () => void;
  onDelete?: () => void;
  isAdmin?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const CONSOLE_LEVELS: ConsoleLogLevel[] = [
  "log",
  "info",
  "warn",
  "error",
];

const isConsoleLogLevel = (value: unknown): value is ConsoleLogLevel =>
  typeof value === "string" &&
  CONSOLE_LEVELS.includes(value as ConsoleLogLevel);

const toJsonValueFromUnknown = (value: unknown): JsonValue => {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value as JsonValue;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? (value as number) : String(value);
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.map((entry) => toJsonValueFromUnknown(entry));
  }

  if (typeof value === "object") {
    const result: Record<string, JsonValue> = {};
    for (const [key, entry] of Object.entries(
      value as Record<string, unknown>,
    )) {
      result[key] = toJsonValueFromUnknown(entry);
    }
    return result;
  }

  return String(value);
};

const extractConsoleLogs = (value: unknown): ConsoleLogEntry[] => {
  if (!Array.isArray(value)) return [];

  const logs: ConsoleLogEntry[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const levelCandidate = entry["level"];
    if (!isConsoleLogLevel(levelCandidate)) continue;
    const timestampValue = entry["timestamp"];
    const timestamp =
      typeof timestampValue === "string"
        ? timestampValue
        : new Date().toISOString();
    const rawArguments = Array.isArray(entry["arguments"])
      ? entry["arguments"]
      : [];

    logs.push({
      level: levelCandidate,
      timestamp,
      arguments: rawArguments.map((arg) => toJsonValueFromUnknown(arg)),
    });
  }

  return logs;
};

const CONSOLE_LEVEL_STYLES: Record<ConsoleLogLevel, string> = {
  log: "text-foreground",
  info: "text-blue-600 dark:text-blue-300",
  warn: "text-amber-600 dark:text-amber-300",
  error: "text-red-600 dark:text-red-300",
};

const formatConsoleArgument = (value: JsonValue): string => {
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value) || typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const ConsoleLogList = ({ logs }: { logs: ConsoleLogEntry[] }) => {
  if (!logs.length) return null;

  return (
    <div className="space-y-2">
      {logs.map((entry, index) => (
        <div
          key={`${entry.timestamp}-${index}`}
          className="rounded-md border border-border/60 bg-background/60 p-3"
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span
              className={`font-semibold uppercase tracking-wide ${CONSOLE_LEVEL_STYLES[entry.level]}`}
            >
              {entry.level}
            </span>
            <span>
              {new Date(entry.timestamp).toLocaleTimeString(undefined, {
                hour12: false,
              })}
            </span>
          </div>
          <div className="mt-2 space-y-1 text-[13px] font-mono text-foreground">
            {entry.arguments.length === 0 ? (
              <span className="text-muted-foreground">(no arguments)</span>
            ) : (
              entry.arguments.map((argument, argIndex) => (
                <pre
                  key={`${entry.timestamp}-${argIndex}`}
                  className="whitespace-pre-wrap break-words"
                >
                  {formatConsoleArgument(argument)}
                </pre>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const extractCodeRunnerError = (
  value: unknown,
): CodeRunnerErrorPayload | null => {
  if (!isRecord(value)) return null;

  const name = typeof value["name"] === "string" ? value["name"] : undefined;
  const message =
    typeof value["message"] === "string" ? value["message"] : undefined;
  const stack = typeof value["stack"] === "string" ? value["stack"] : undefined;
  const code = typeof value["code"] === "string" ? value["code"] : undefined;

  if (!name && !message && !stack && !code) {
    return null;
  }

  return {
    message: message ?? "Unknown error",
    ...(name && { name }),
    ...(stack && { stack }),
    ...(code && { code }),
  } satisfies CodeRunnerErrorPayload;
};

const extractCodeRunnerResponse = (
  value: unknown,
): CodeRunnerResponsePayload | null => {
  if (!isRecord(value)) return null;

  const error = extractCodeRunnerError(value["error"]);
  const executionTime =
    typeof value["executionTime"] === "number"
      ? value["executionTime"]
      : undefined;
  const output =
    typeof value["output"] === "string"
      ? value["output"]
      : value["output"] === null
        ? null
        : undefined;
  const consoleLogs = extractConsoleLogs(value["consoleLogs"]);

  if (
    error ||
    executionTime !== undefined ||
    output !== undefined ||
    consoleLogs.length > 0
  ) {
    return {
      error: error ?? null,
      executionTime: typeof executionTime === "number" ? executionTime : null,
      output: output ?? null,
      consoleLogs,
    } satisfies CodeRunnerResponsePayload;
  }

  return null;
};

const createCodeRunnerExecutionError = (
  payload: unknown,
): CodeRunnerExecutionError => {
  const extracted = extractCodeRunnerError(payload);

  return {
    type: "code-runner",
    name: extracted?.name ?? "CodeRunnerError",
    message: extracted?.message ?? "The agent runner returned an error.",
    ...(extracted?.stack !== undefined && { stack: extracted.stack }),
    ...(extracted?.code !== undefined && { code: extracted.code }),
    raw: payload,
  };
};

const createExecutionErrorFromParsedSupabase = (
  error: ParsedSupabaseFunctionError<unknown>,
): SupabaseFunctionExecutionError => {
  const base: SupabaseFunctionExecutionError = {
    type: "supabase-function",
    name: error.name,
    message:
      error instanceof SupabaseParseError
        ? `${error.message} (${error.phase})`
        : error.message,
    ...(error.stack !== undefined && { stack: error.stack }),
    raw: error,
  };

  if (isSupabaseResponseError(error)) {
    const responseBody =
      error.body !== undefined ? extractCodeRunnerResponse(error.body) : null;
    const responseText =
      typeof error.body === "string" ? error.body : undefined;

    return {
      ...base,
      ...(error.status !== undefined ? { status: error.status } : {}),
      ...(responseBody !== null ? { responseBody } : {}),
      ...(responseText !== undefined ? { responseText } : {}),
    };
  }

  return base;
};

const createGenericExecutionError = (error: unknown): GenericExecutionError => {
  if (error instanceof Error) {
    return {
      type: "generic",
      name: error.name,
      message: error.message,
      ...(error.stack !== undefined && { stack: error.stack }),
      raw: error,
    };
  }

  return {
    type: "generic",
    name: "Error",
    message: String(error),
    raw: error,
  };
};

const createExecutionErrorFromUnknown = (error: unknown): ExecutionError => {
  if (error instanceof SupabaseFunctionError) {
    return createExecutionErrorFromParsedSupabase(
      error as ParsedSupabaseFunctionError<unknown>,
    );
  }

  return createGenericExecutionError(error);
};

export const CodeView = ({
  code,
  userEmail,
  onUpdate,
  onDelete,
  isAdmin = false,
}: CodeViewProps) => {
  const user = useAuthenticatedUser();
  const [codeContent, setCodeContent] = useState(code.code_content || "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(code.title);
  const [isPublic, setIsPublic] = useState(code.is_public);
  const [sandboxEnabled, setSandboxEnabled] = useState(code.sandbox ?? true);
  const [testInput, setTestInput] = useState("");
  const [executionResult, setExecutionResult] =
    useState<ExecutionResult | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionMode, setExecutionMode] = useState<
    "browser" | "server" | null
  >(null);
  const { toast } = useToast();
  const isOwner = user?.id === code.user_id;
  const revalidator = useRevalidator();
  const saveFetcher = useFetcher<typeof workspaceSidebarAction>();
  const visibilityFetcher = useFetcher<typeof workspaceSidebarAction>();
  const titleFetcher = useFetcher<typeof workspaceSidebarAction>();
  const deleteFetcher = useFetcher<typeof workspaceSidebarAction>();
  const sandboxFetcher = useFetcher<typeof workspaceSidebarAction>();
  const saveResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const visibilityResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const titleResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const deleteResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const sandboxResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const pendingVisibilityRef = useRef<boolean | null>(null);
  const pendingTitleRef = useRef<string | null>(null);
  const pendingSandboxRef = useRef<boolean | null>(null);
  const isSavingCode = saveFetcher.state !== "idle";
  const isVisibilityPending = visibilityFetcher.state !== "idle";
  const isTitlePending = titleFetcher.state !== "idle";
  const isDeletePending = deleteFetcher.state !== "idle";
  const isSandboxPending = sandboxFetcher.state !== "idle";
  const submitCodeAction = (
    fetcher: FetcherWithComponents<WorkspaceSidebarActionResult>,
    intent: WorkspaceSidebarActionIntent,
    fields: Record<string, string>,
  ) => {
    const formData = new FormData();
    formData.set("intent", intent);
    Object.entries(fields).forEach(([key, value]) => formData.set(key, value));
    fetcher.submit(formData, {
      method: "post",
      action: WORKSPACE_SIDEBAR_ACTION_PATH,
    });
  };

  // Sync all states when code changes
  useEffect(() => {
    setCodeContent(code.code_content || "");
    setEditedTitle(code.title);
    setIsPublic(code.is_public);
    setSandboxEnabled(code.sandbox ?? true);
  }, [code.id, code.code_content, code.title, code.is_public, code.sandbox]);

  const handleCodeSave = () => {
    submitCodeAction(saveFetcher, "codeUpdateContent", {
      entityId: code.id,
      codeContent,
    });
  };

  const handlePublicToggle = (checked: boolean) => {
    pendingVisibilityRef.current = checked;
    submitCodeAction(visibilityFetcher, "codeTogglePublic", {
      entityId: code.id,
      isPublic: String(checked),
    });
  };

  const handleSandboxToggle = (checked: boolean) => {
    if (!isAdmin) return;
    pendingSandboxRef.current = checked;
    submitCodeAction(sandboxFetcher, "codeToggleSandbox", {
      entityId: code.id,
      sandbox: String(checked),
    });
  };

  const handleTitleEdit = () => {
    if (!editedTitle.trim() || editedTitle === code.title) {
      setIsEditingTitle(false);
      setEditedTitle(code.title);
      return;
    }

    const trimmed = editedTitle.trim();
    pendingTitleRef.current = trimmed;
    submitCodeAction(titleFetcher, "updateCodeTitle", {
      entityId: code.id,
      title: trimmed,
    });
  };

  const handleDelete = () => {
    submitCodeAction(deleteFetcher, "codeDelete", {
      entityId: code.id,
    });
  };

  useEffect(() => {
    if (saveFetcher.state !== "idle" || !saveFetcher.data) return;
    if (saveResultRef.current === saveFetcher.data) return;
    saveResultRef.current = saveFetcher.data;

    if (saveFetcher.data.ok) {
      toast({ title: "Success", description: "Agent saved successfully" });
      revalidator.revalidate();
      onUpdate?.();
    } else {
      toast({
        title: "Error",
        description: saveFetcher.data.error || "Failed to save agent",
        variant: "destructive",
      });
    }
  }, [saveFetcher.state, saveFetcher.data, toast, revalidator, onUpdate]);

  useEffect(() => {
    if (visibilityFetcher.state !== "idle" || !visibilityFetcher.data) return;
    if (visibilityResultRef.current === visibilityFetcher.data) return;
    visibilityResultRef.current = visibilityFetcher.data;

    if (visibilityFetcher.data.ok) {
      if (pendingVisibilityRef.current !== null) {
        setIsPublic(pendingVisibilityRef.current);
      }
      toast({
        title: "Success",
        description: `Agent is now ${pendingVisibilityRef.current ? "public" : "private"}`,
      });
      pendingVisibilityRef.current = null;
      revalidator.revalidate();
      onUpdate?.();
    } else {
      pendingVisibilityRef.current = null;
      toast({
        title: "Error",
        description:
          visibilityFetcher.data.error || "Failed to update agent visibility",
        variant: "destructive",
      });
    }
  }, [
    visibilityFetcher.state,
    visibilityFetcher.data,
    toast,
    revalidator,
    onUpdate,
  ]);

  useEffect(() => {
    if (sandboxFetcher.state !== "idle" || !sandboxFetcher.data) return;
    if (sandboxResultRef.current === sandboxFetcher.data) return;
    sandboxResultRef.current = sandboxFetcher.data;

    if (sandboxFetcher.data.ok) {
      if (pendingSandboxRef.current !== null) {
        setSandboxEnabled(pendingSandboxRef.current);
      }

      const enabled = pendingSandboxRef.current ?? sandboxEnabled;
      toast({
        title: "Execution mode updated",
        description: enabled
          ? "Sandbox enforcement restored for this agent."
          : "Sandbox disabled. Runs will execute without QuickJS isolation.",
      });
      pendingSandboxRef.current = null;
      revalidator.revalidate();
      onUpdate?.();
    } else {
      toast({
        title: "Error",
        description:
          sandboxFetcher.data.error || "Failed to update sandbox mode",
        variant: "destructive",
      });
      pendingSandboxRef.current = null;
      setSandboxEnabled(code.sandbox ?? true);
    }
  }, [
    sandboxFetcher.state,
    sandboxFetcher.data,
    toast,
    revalidator,
    onUpdate,
    sandboxEnabled,
    code.sandbox,
  ]);

  useEffect(() => {
    if (titleFetcher.state !== "idle" || !titleFetcher.data) return;
    if (titleResultRef.current === titleFetcher.data) return;
    titleResultRef.current = titleFetcher.data;

    if (titleFetcher.data.ok) {
      const nextTitle = pendingTitleRef.current ?? code.title;
      toast({
        title: "Success",
        description: "Agent name updated successfully",
      });
      setIsEditingTitle(false);
      setEditedTitle(nextTitle);
      revalidator.revalidate();
      onUpdate?.();
    } else {
      toast({
        title: "Error",
        description: titleFetcher.data.error || "Failed to update agent name",
        variant: "destructive",
      });
      setEditedTitle(code.title);
      setIsEditingTitle(false);
    }

    pendingTitleRef.current = null;
  }, [
    titleFetcher.state,
    titleFetcher.data,
    toast,
    revalidator,
    onUpdate,
    code.title,
  ]);

  useEffect(() => {
    if (deleteFetcher.state !== "idle" || !deleteFetcher.data) return;
    if (deleteResultRef.current === deleteFetcher.data) return;
    deleteResultRef.current = deleteFetcher.data;

    if (deleteFetcher.data.ok) {
      toast({ title: "Success", description: "Agent deleted successfully" });
      setDeleteDialogOpen(false);
      revalidator.revalidate();
      onDelete?.();
    } else {
      toast({
        title: "Error",
        description: deleteFetcher.data.error || "Failed to delete agent",
        variant: "destructive",
      });
    }
  }, [deleteFetcher.state, deleteFetcher.data, toast, revalidator, onDelete]);

  const executeCodeInBrowser = async (useSandbox: boolean) => {
    setIsExecuting(true);
    setExecutionResult(null);
    setExecutionMode("browser");

    try {
      const execute = useSandbox
        ? createSandboxExecute()
        : createUnsafeExecute();

      const result = await executeUserCode({
        code: codeContent,
        input: testInput,
        execute,
      });

      if (result.ok) {
        setExecutionResult({
          success: true,
          output: result.output,
          consoleLogs: result.consoleLogs,
          executionTime: result.executionTime,
        });
      } else {
        setExecutionResult({
          success: false,
          error: createExecutionErrorFromUnknown(result.error),
          consoleLogs: result.consoleLogs,
          executionTime: result.executionTime,
        });
      }
    } catch (error) {
      setExecutionResult({
        success: false,
        error: createExecutionErrorFromUnknown(error),
        consoleLogs: [],
        executionTime: null,
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const executeCodeOnServer = async (useSandbox: boolean) => {
    setIsExecuting(true);
    setExecutionResult(null);
    setExecutionMode("server");

    try {
      const result = await invokeFunction(CodeRunnerContract, {
        client: supabase,
        body: {
          codeId: code.id,
          input: testInput,
          sandbox: useSandbox,
        },
      });

      if (!result.ok) {
        const parsedError = createExecutionErrorFromParsedSupabase(result.error);
        setExecutionResult({
          success: false,
          error: parsedError,
          consoleLogs: parsedError.responseBody?.consoleLogs ?? [],
          executionTime: parsedError.responseBody?.executionTime ?? null,
        });
        return;
      }

      const data = result.data;

      if (data.error) {
        setExecutionResult({
          success: false,
          error: createCodeRunnerExecutionError(data.error),
          consoleLogs: data.consoleLogs ?? [],
          executionTime:
            typeof data.executionTime === "number" ? data.executionTime : null,
        });
        return;
      }

      setExecutionResult({
        success: true,
        output: data.output ?? "(no return value)",
        consoleLogs: data.consoleLogs ?? [],
        executionTime:
          typeof data.executionTime === "number" ? data.executionTime : null,
      });
    } catch (error) {
      setExecutionResult({
        success: false,
        error: createExecutionErrorFromUnknown(error),
        consoleLogs: [],
        executionTime: null,
      });
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="p-8 w-full">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTitleEdit();
                    if (e.key === "Escape") {
                      setIsEditingTitle(false);
                      setEditedTitle(code.title);
                    }
                  }}
                  className="text-3xl font-bold h-12 w-96"
                  autoFocus
                  aria-label="Agent name"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleTitleEdit}
                  className="h-10 w-10 p-0"
                  aria-label="Save agent name"
                  disabled={isTitlePending}
                >
                  <Check className="h-5 w-5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsEditingTitle(false);
                    setEditedTitle(code.title);
                  }}
                  className="h-10 w-10 p-0"
                  aria-label="Cancel agent name edit"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-foreground">
                  {code.title}
                </h1>
                {isOwner && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingTitle(true)}
                    className="h-8 w-8 p-0"
                    aria-label="Edit agent name"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleCodeSave}
              variant="default"
              size="sm"
              disabled={!isOwner || isSavingCode}
              className="flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              Save Agent
            </Button>
            {isOwner && (
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-destructive hover:text-destructive"
                disabled={isDeletePending}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
          </div>
        </div>

        <p className="text-muted-foreground">JavaScript Agent Configuration</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Owner
            </h3>
            <p className="text-lg text-foreground">
              {code.owner_email || userEmail || "Unknown"}
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Created
            </h3>
            <p className="text-lg text-foreground">
              {formatDistanceToNow(new Date(code.created_at), {
                addSuffix: true,
              })}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {new Date(code.created_at).toLocaleDateString()}
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Last Updated
            </h3>
            <p className="text-lg text-foreground">
              {formatDistanceToNow(new Date(code.updated_at), {
                addSuffix: true,
              })}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {new Date(code.updated_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Configuration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Agent ID:</span>
              <span className="text-foreground font-mono">{code.id}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Visibility:</span>
                {isPublic ? (
                  <Badge variant="default" className="flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Public
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    <Lock className="h-3 w-3" />
                    Private
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="public-switch"
                  className="text-sm cursor-pointer"
                >
                  {isPublic ? "Public" : "Private"}
                </Label>
                <Switch
                  id="public-switch"
                  checked={pendingVisibilityRef.current ?? isPublic}
                  onCheckedChange={handlePublicToggle}
                  disabled={!isOwner || isVisibilityPending}
                />
              </div>
            </div>
          </div>
          <div className="mt-6 space-y-2">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <span className="text-muted-foreground text-sm">
                  Execution Mode:
                </span>
                <div className="mt-1">
                  <Badge
                    variant={sandboxEnabled ? "secondary" : "destructive"}
                    className="flex items-center gap-1 w-fit"
                  >
                    {sandboxEnabled ? (
                      <Shield className="h-3 w-3" />
                    ) : (
                      <ShieldAlert className="h-3 w-3" />
                    )}
                    {sandboxEnabled ? "Sandboxed" : "Unsafe"}
                  </Badge>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="sandbox-switch"
                    className="text-sm cursor-pointer"
                  >
                    {sandboxEnabled ? "Sandbox Enabled" : "Sandbox Disabled"}
                  </Label>
                  <Switch
                    id="sandbox-switch"
                    checked={pendingSandboxRef.current ?? sandboxEnabled}
                    onCheckedChange={handleSandboxToggle}
                    disabled={isSandboxPending}
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {isAdmin
                ? "Only admins can change sandbox mode. Unsafe runs execute outside the QuickJS isolation."
                : "Sandbox mode is managed by admins. Contact them if this setting needs to change."}
            </p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Agent Script
            </h3>
            <Badge variant="secondary" className="flex items-center gap-1">
              <CodeIcon className="h-3 w-3" />
              JavaScript
            </Badge>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <CodeMirror
              value={codeContent}
              height="400px"
              extensions={[javascript()]}
              onChange={(value) => setCodeContent(value)}
              theme="dark"
              readOnly={!isOwner}
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Test Panel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="test-input">Test Input</Label>
              <Textarea
                id="test-input"
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                placeholder="Enter test input..."
                className="mt-2 min-h-[100px]"
              />
            </div>
            <div className="flex gap-1">
              <Button
                onClick={() => executeCodeOnServer(sandboxEnabled)}
                disabled={isExecuting}
                className="flex-1"
              >
                <Server className="mr-2 h-4 w-4" />
                {isExecuting && executionMode === "server"
                  ? "Running..."
                  : sandboxEnabled
                    ? "Run Agent (Sandboxed)"
                    : "Run Agent (Unsafe)"}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={isExecuting}
                    className="px-2"
                    variant="outline"
                    aria-label="Execution options"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => executeCodeInBrowser(true)}>
                    <MonitorPlay className="mr-2 h-4 w-4" />
                    Run in Browser (Local)
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuItem
                        onClick={() => executeCodeOnServer(false)}
                      >
                        <Server className="mr-2 h-4 w-4" />
                        <span className="text-orange-600 dark:text-orange-400">
                          Run on Server (Unsafe)
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => executeCodeInBrowser(false)}
                      >
                        <MonitorPlay className="mr-2 h-4 w-4" />
                        <span className="text-orange-600 dark:text-orange-400">
                          Run in Browser (Unsafe)
                        </span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {executionResult && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Output</Label>
                    {typeof executionResult.executionTime === "number" && (
                      <p className="text-xs text-muted-foreground">
                        Execution time: {executionResult.executionTime}ms
                      </p>
                    )}
                  </div>
                  {executionMode && (
                    <Badge
                      variant="outline"
                      className="text-xs"
                      data-testid="code-view-execution-mode"
                    >
                      {executionMode === "browser" ? (
                        <>
                          <MonitorPlay className="mr-1 h-3 w-3" />
                          Browser
                        </>
                      ) : (
                        <>
                          <Server className="mr-1 h-3 w-3" />
                          Server
                        </>
                      )}
                    </Badge>
                  )}
                </div>
                <div
                  className={`p-4 rounded-lg border ${executionResult.success ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800" : "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800"}`}
                >
                  {executionResult.success ? (
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                          Result:
                        </p>
                        <pre className="text-sm text-green-800 dark:text-green-200 whitespace-pre-wrap mt-1">
                          {executionResult.output ?? "(no return value)"}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-red-900 dark:text-red-100">
                          {executionResult.error.name}:{" "}
                          {executionResult.error.message}
                        </p>
                        {executionResult.error.type === "code-runner" &&
                          executionResult.error.code && (
                            <p className="text-xs text-red-700 dark:text-red-200 mt-1">
                              Error code: {executionResult.error.code}
                            </p>
                          )}
                        {executionResult.error.type === "supabase-function" &&
                          executionResult.error.status && (
                            <p className="text-xs text-red-700 dark:text-red-200 mt-1">
                              HTTP status: {executionResult.error.status}
                            </p>
                          )}
                      </div>
                      {executionResult.error.stack && (
                        <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap">
                          {executionResult.error.stack}
                        </pre>
                      )}
                      {executionResult.error.type === "code-runner" &&
                        executionResult.error.raw !== undefined && (
                          <div>
                            <p className="text-xs font-semibold text-red-900 dark:text-red-100">
                              Agent Runner Payload
                            </p>
                            <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap mt-1">
                              {typeof executionResult.error.raw === "string"
                                ? executionResult.error.raw
                                : JSON.stringify(
                                    executionResult.error.raw,
                                    null,
                                    2,
                                  )}
                            </pre>
                          </div>
                        )}
                      {executionResult.error.type === "supabase-function" &&
                        executionResult.error.responseBody && (
                          <div>
                            <p className="text-xs font-semibold text-red-900 dark:text-red-100">
                              Edge Function Payload
                            </p>
                            <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap mt-1">
                              {JSON.stringify(
                                executionResult.error.responseBody,
                                null,
                                2,
                              )}
                            </pre>
                          </div>
                        )}
                      {executionResult.error.type === "supabase-function" &&
                        !executionResult.error.responseBody &&
                        executionResult.error.responseText && (
                          <div>
                            <p className="text-xs font-semibold text-red-900 dark:text-red-100">
                              Edge Function Response
                            </p>
                            <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap mt-1">
                              {executionResult.error.responseText}
                            </pre>
                          </div>
                      )}
                    </div>
                  )}
                  {executionResult.consoleLogs.length > 0 && (
                    <div
                      className="mt-4 space-y-2"
                      data-testid="code-view-console-output"
                    >
                      <p className="text-sm font-semibold text-foreground">
                        Console Output
                      </p>
                      <ConsoleLogList logs={executionResult.consoleLogs} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agent</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this agent? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletePending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
